import $, { Cash } from "cash-dom";

/**
 * Component for loading videos via Vimeo/Youtube
 */
export default class EmbedVideoLoader {
    /**
     * Singleton instance of the component
     */
    static #instance: EmbedVideoLoader;

    /**
     * Define UI elements
     */
    protected ui = {
        videoPlayer: ".js-video-player",
        videoWrapper: ".js-video-wrapper",
        iframeLoader: ".js-iframe-loader",
    };

    /**
     * EmbedVideoLoader state classes on container
     * @type {object}
     */
    protected stateClasses = {
        ACTIVE: "is-active",
        PLAYING: "is-playing",
        HAS_ACTIVE_VIDEO: "has-active-video",
    };

    /**
     * VIDEO constructor
     */
	protected constructor() {
        this.init();
    }

    /**
     * Returns the singleton instance of the component
     */
    public static getInstance(): EmbedVideoLoader {
        if (!EmbedVideoLoader.#instance) {
            EmbedVideoLoader.#instance = new EmbedVideoLoader();
        }

        return EmbedVideoLoader.#instance;
    }

    /**
     * Initializes event listeners
     */
    protected init(): void {
        this.bindVideoPlayers($(this.ui.videoPlayer));
    }

    /**
     * Binds click events to video players
     */
    protected bindVideoPlayers($videoPlayers: Cash): void {
        if ($videoPlayers.length > 0) {
            $videoPlayers.off("click").on("click", (event) => {
                this.activateVideoPlayer($(event.currentTarget));
            });
        }
    }

    /**
     * Public method to reinitialize video players in a specific container
     */
    public reinitialize($container?: Cash): void {
        const $videoPlayers = $container
            ? $container.find(this.ui.videoPlayer)
            : $(this.ui.videoPlayer);

        this.bindVideoPlayers($videoPlayers);
    }

    /**
     * Activates the selected video player
     */
    protected activateVideoPlayer($videoPlayer: Cash): void {
        if (!$videoPlayer.hasClass(this.stateClasses.ACTIVE)) {
            // Reset all other video players
            this.resetAllVideoPlayers();

            // Add playing class to remove preview video
            $videoPlayer.addClass(this.stateClasses.ACTIVE);

            // Add active class to specific parent if available
            const $videoWrapper = $videoPlayer.closest(this.ui.videoWrapper);
            if ($videoWrapper.length > 0) {
                $videoWrapper.addClass(this.stateClasses.HAS_ACTIVE_VIDEO);
            }

            // Load iframe
            const $iframeLoader = $videoPlayer.find(this.ui.iframeLoader);
            if ($iframeLoader.length > 0) {
                const markup = this.getIframeMarkupConfig($iframeLoader);
                $iframeLoader.replaceWith(markup);
            }

            // Set iframe src
            const $iframe: Cash = $videoPlayer.find("iframe");

            if ($iframe.length > 0) {
                const dataSrcValue = $iframe.attr("data-src");

                if (dataSrcValue) {
                    $iframe.attr("src", dataSrcValue).removeAttr("data-src");
                }

                $iframe.on("load", () => {
                    $videoPlayer.addClass(this.stateClasses.PLAYING);
                    $iframe.off("load"); // Remove event listener
                });
            }
        }
    }

	/**
	 * Resets a specific player to initial state
	 */
	public resetVideo($videoPlayer: Cash): void {
		if (!$videoPlayer.length) return;

		$videoPlayer.removeClass(`${this.stateClasses.ACTIVE} ${this.stateClasses.PLAYING}`);
		const $videoWrapper = $videoPlayer.closest(this.ui.videoWrapper);
		if ($videoWrapper.length) {
			$videoWrapper.removeClass(this.stateClasses.HAS_ACTIVE_VIDEO);
		}

		const $iframe = $videoPlayer.find("iframe");
		if ($iframe.length > 0 && $iframe.attr("src")) {
			const srcValue = $iframe.attr("src") ?? "";
			$iframe.attr("data-src", srcValue).removeAttr("src");
		}
	}

    /**
     * Resets all video players to initial state
     */
    private resetAllVideoPlayers(): void {
        const $videoPlayers = $(this.ui.videoPlayer);

        $videoPlayers.removeClass(
            `${this.stateClasses.ACTIVE} ${this.stateClasses.PLAYING}`
        );
        $(this.ui.videoWrapper).removeClass(this.stateClasses.HAS_ACTIVE_VIDEO);

        $videoPlayers.each((_, videoPlayer) => {
            const $videoPlayer = $(videoPlayer);
            const $iframe = $videoPlayer.find("iframe");

            if ($iframe.length > 0 && $iframe.attr("src")) {
				const srcValue = $iframe.attr("src") ?? "";
				$iframe.attr("data-src", srcValue).removeAttr("src");
            }
        });
    }

    /**
     * Constructs the iframe markup
     */
    protected getIframeMarkupConfig($element: Cash): string {
        const title = $element.data("title");
        const src = $element.data("src");

		// Build iframe attributes for Vimeo embed
		const iframeAttributes = Object.entries({
			    class: "video-player__embed",
			    "data-ready": "true",
			    allow: "autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope",
			    src: src,
			    title: title,
        }).filter(([_, value]) => typeof value !== "boolean" || value)
          .map(([key, value]) =>
                typeof value === "boolean" ? key : `${key}="${value}"`
            )
          .join(" ");

        return `<iframe ${iframeAttributes}></iframe>`;
    }
}
